import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../components/feature/LanguageSwitcher';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seoHelpers';

export default function BookingPage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://norwebas.no';

  // SEO Configuration
  useSEO({
    title: 'Book Konsultasjon | NorWeb AS - Webutvikling & Webdesign Hamar',
    description: 'Book en gratis konsultasjon med NorWeb AS. Vi diskuterer dine behov for webutvikling, webdesign og nettside utvikling. 15 minutters møte via video eller telefon.',
    keywords: 'book konsultasjon, webutvikling møte, webdesign konsultasjon, NorWeb booking, gratis konsultasjon nettside',
    canonical: `${siteUrl}/booking`,
    ogImage: 'https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png',
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema({
          name: 'Book Konsultasjon - NorWeb AS',
          description: 'Book en gratis konsultasjon med NorWeb AS for webutvikling og webdesign.',
          url: '/booking'
        }),
        generateBreadcrumbSchema([
          { name: 'Hjem', url: siteUrl },
          { name: 'Booking', url: `${siteUrl}/booking` }
        ]),
        {
          '@type': 'Service',
          'serviceType': 'Konsultasjon',
          'provider': {
            '@type': 'LocalBusiness',
            'name': 'NorWeb AS',
            'url': siteUrl
          },
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'NOK',
            'description': 'Gratis 15 minutters konsultasjon'
          }
        }
      ]
    }
  });

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans overflow-x-hidden">
      {/* Skip to main content */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-fuchsia-600 focus:text-white focus:rounded-lg"
      >
        Hopp til hovedinnhold
      </a>

      {/* Navigation */}
      <header>
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-sm'
          }`}
          aria-label="Hovednavigasjon"
        >
          <div className="w-full px-4 sm:px-6 lg:px-12 py-3">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-2 cursor-pointer" aria-label="NorWeb AS - Gå til forsiden">
                <img 
                  src="https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png"
                  alt="NorWeb AS logo" 
                  className="h-7 sm:h-8 w-auto"
                  width="100"
                  height="32"
                />
              </a>
              <div className="flex items-center gap-3 sm:gap-4">
                <LanguageSwitcher />
                <a
                  href="/"
                  className="px-4 py-2 bg-slate-800 text-white text-xs sm:text-sm font-medium rounded-md hover:bg-slate-700 transition-all whitespace-nowrap cursor-pointer focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                >
                  {t('booking.backHome')}
                </a>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <main id="main-content">
        {/* Hero Section */}
        <section className="pt-20 sm:pt-24 pb-8 sm:pb-10 px-4 sm:px-6 lg:px-12 bg-gradient-to-br from-violet-900 to-fuchsia-900" aria-labelledby="booking-title">
          <div className="max-w-3xl mx-auto text-center">
            <h1 id="booking-title" className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              {t('booking.title')}
            </h1>
            <p className="text-sm sm:text-base text-violet-200 max-w-xl mx-auto px-2">
              {t('booking.subtitle')}
            </p>
          </div>
        </section>

        {/* Calendly Section */}
        <section className="py-8 sm:py-10 px-4 sm:px-6 lg:px-12" aria-label="Booking kalender">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg">
              {/* Calendly Inline Widget */}
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/94121xxx/30min"
                style={{ minWidth: '320px', height: '1100px', overflow: 'hidden' }}
                role="application"
                aria-label="Calendly booking widget"
                title="Velg et tidspunkt for konsultasjon"
              ></div>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <article className="bg-white rounded-lg p-4 shadow-sm">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-lg flex items-center justify-center mb-3" aria-hidden="true">
                  <i className="ri-time-line text-lg text-white"></i>
                </div>
                <h2 className="text-sm font-bold text-slate-800 mb-1">{t('booking.duration')}</h2>
                <p className="text-xs text-slate-600 leading-relaxed">{t('booking.durationDesc')}</p>
              </article>

              <article className="bg-white rounded-lg p-4 shadow-sm">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-lg flex items-center justify-center mb-3" aria-hidden="true">
                  <i className="ri-video-line text-lg text-white"></i>
                </div>
                <h2 className="text-sm font-bold text-slate-800 mb-1">{t('booking.format')}</h2>
                <p className="text-xs text-slate-600 leading-relaxed">{t('booking.formatDesc')}</p>
              </article>

              <article className="bg-white rounded-lg p-4 shadow-sm">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-lg flex items-center justify-center mb-3" aria-hidden="true">
                  <i className="ri-calendar-check-line text-lg text-white"></i>
                </div>
                <h2 className="text-sm font-bold text-slate-800 mb-1">{t('booking.preparation')}</h2>
                <p className="text-xs text-slate-600 leading-relaxed">{t('booking.preparationDesc')}</p>
              </article>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 px-4 sm:px-6 lg:px-12 mt-8" role="contentinfo">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/44a7a8c2159e827a3b36d551e672e0e2.png" 
                alt="NorWeb AS logo" 
                className="h-7 sm:h-8 w-auto"
                width="100"
                height="32"
                loading="lazy"
              />
            </div>
            <nav aria-label="Footer navigasjon">
              <ul className="flex items-center gap-4 sm:gap-6 list-none p-0 m-0">
                <li>
                  <a href="/#contact-form" className="text-slate-400 hover:text-white transition-colors text-xs sm:text-sm cursor-pointer">
                    {t('footer.contact')}
                  </a>
                </li>
                <li>
                  <a href="/" className="text-slate-400 hover:text-white transition-colors text-xs sm:text-sm cursor-pointer">
                    {t('booking.backHome')}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
          <div className="border-t border-slate-700 mt-6 pt-6">
            <p className="text-xs text-slate-500 text-center">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
